import { speciesById } from "../data/species.ts";
import { STATUS_META, type StatusId } from "../data/status.ts";
import { TYPE_META, type TypeId } from "../data/types.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import type { Gfx } from "../engine/gfx.ts";
import type { BattleMon } from "../game/combat.ts";
import { expToNext, LEVEL_CAP, maxHp } from "../game/mon.ts";
import { cursor, focus, glass, hint, hpTone, meter, typeBadge } from "../ui/kit.ts";
import { mix, TYPE_COL, UI, withA, type Col } from "./pal.ts";

/** Bottom command deck geometry. */
export const DECK = { x: 6, y: 160, w: 372, h: 50 };
/** Where the settled plates print the two creature names (for tools and tests). */
export const PLATE_NAME = { foe: { x: 18, y: 16 }, you: { x: 216, y: 112 } };

/** Condition chip (SCR / FRZ / LAG / AFK / FUD) in the status colour; returns its width. */
export function statusChip(g: Gfx, x: number, y: number, s: StatusId): number {
  const meta = STATUS_META[s];
  const w = measureText(meta.chip) + 8;
  g.rrect(x, y, w, 11, 4, meta.col);
  g.hline(x + 3, y + 1, w - 6, withA(UI.white, 120));
  g.text(x + 4, y + 2, meta.chip, UI.paperInk);
  return w;
}

function slideIn(t: number, from: number): number {
  const u = Math.max(0, Math.min(1, t));
  return Math.round((1 - (1 - (1 - u) ** 3)) * from);
}

/** Foe plate: name, level, type badges, HP meter (no numbers) and a WILD / RIVAL / BOSS tag. */
export function foePlate(g: Gfx, b: BattleMon, rival: boolean | "BOSS", t: number): void {
  const x = 8 - slideIn(t, 180), y = 10, w = 164, h = 40;
  const sp = speciesById(b.mon.speciesId);
  glass(g, x, y, w, h, { r: 6 });
  g.rrect(x + 1, y + 1, 4, h - 2, 2, TYPE_COL[sp.types[0]!]!.fill);
  g.textShadow(x + 10, y + 6, b.mon.nick.slice(0, 14), UI.text, UI.ink);
  if (b.mon.shiny) g.textShadow(x + 13 + measureText(b.mon.nick.slice(0, 14)), y + 6, "✦", UI.goldHi, UI.ink);
  const lv = `Lv${b.mon.level}`;
  g.textShadow(x + w - measureText(lv) - 8, y + 6, lv, UI.gold, UI.ink);
  let bx = x + 10;
  for (const type of sp.types) bx += typeBadge(g, bx, y + 16, type, true) + 3;
  if (b.status && b.faint < 1) statusChip(g, bx + 1, y + 16, b.status);
  const tag = b.faint === 1 ? "OUT" : rival === "BOSS" ? "BOSS" : rival ? "RIVAL" : "WILD";
  const tw = measureText(tag) + 8;
  g.rrect(x + w - tw - 7, y + 16, tw, 11, 4, tag === "BOSS" ? UI.pink : tag === "RIVAL" ? UI.gold : UI.bg3);
  g.text(x + w - tw - 3, y + 18, tag, tag === "WILD" ? UI.textDim : UI.paperInk);
  const hp = maxHp(b.mon);
  const shown = Math.max(0, b.shownHp / hp), live = Math.max(0, b.mon.hp / hp);
  g.text(x + 10, y + 30, "HP", UI.textDim);
  meter(g, x + 24, y + 30, w - 34, 6, Math.min(live, shown), { ghost: Math.max(live, shown), tone: hpTone(shown) });
}

/** Our plate: name, level, HP meter with numbers, EXP strip. */
export function youPlate(g: Gfx, b: BattleMon, t: number): void {
  const w = 170, h = 47;
  const x = 384 - w - 8 + slideIn(t, 200), y = 106;
  const sp = speciesById(b.mon.speciesId);
  glass(g, x, y, w, h, { r: 6 });
  g.rrect(x + 1, y + 1, 4, h - 2, 2, TYPE_COL[sp.types[0]!]!.fill);
  g.textShadow(x + 10, y + 6, b.mon.nick.slice(0, 14), UI.text, UI.ink);
  if (b.mon.shiny) g.textShadow(x + 13 + measureText(b.mon.nick.slice(0, 14)), y + 6, "✦", UI.goldHi, UI.ink);
  const lv = `Lv${b.mon.level}`;
  g.textShadow(x + w - measureText(lv) - 8, y + 6, lv, UI.gold, UI.ink);
  const hp = maxHp(b.mon);
  const shown = Math.max(0, b.shownHp / hp), live = Math.max(0, b.mon.hp / hp);
  g.text(x + 10, y + 18, "HP", UI.textDim);
  meter(g, x + 24, y + 18, w - 34, 6, Math.min(live, shown), { ghost: Math.max(live, shown), tone: hpTone(shown) });
  const readout = b.faint === 1 ? "OUT" : `${Math.ceil(b.shownHp)}/${hp}`;
  g.textShadow(x + w - measureText(readout) - 9, y + 27, readout, UI.text, UI.ink);
  let bx = x + 10;
  for (const type of sp.types) bx += typeBadge(g, bx, y + 26, type, true) + 3;
  if (b.status && b.faint < 1) statusChip(g, bx + 1, y + 26, b.status);
  const next = expToNext(b.mon.level);
  const e = b.mon.level >= LEVEL_CAP ? 1 : Math.max(0, Math.min(1, b.mon.exp / next));
  g.text(x + 10, y + 38, "EXP", UI.textMute);
  meter(g, x + 30, y + 40, w - 40, 3, e, { tone: { fill: UI.teal, hi: [150, 255, 240, 255], lo: UI.tealDk }, trough: [30, 34, 60, 255] });
}

/** Message deck with a speaker label and the ▼ advance cue. */
export function messageDeck(g: Gfx, text: string, more: boolean, now: number, reduced: boolean, label?: string): void {
  const { x, y, w, h } = DECK;
  glass(g, x, y, w, h, { r: 7 });
  if (label) {
    const lw = measureText(label) + 12;
    g.rrect(x + 10, y - 6, lw, 12, 5, UI.ink);
    g.gradV(x + 11, y - 5, lw - 2, 10, UI.bg3, UI.bg2, 4);
    g.text(x + 16, y - 3, label, UI.textDim);
  }
  const lines = wrapPx(text, w - 30).slice(0, 3);
  lines.forEach((line, i) => g.textShadow(x + 12, y + 9 + i * 12, line, UI.text, UI.ink));
  if (more) {
    const b = reduced ? 0 : Math.round(Math.abs(Math.sin(now / 170)) * 2);
    g.text(x + w - 16, y + h - 13 + b, "▼", UI.gold);
  }
}

const CMDS: { label: string; tone: Col; icon: string }[] = [
  { label: "FIGHT", tone: [255, 96, 120, 255], icon: "★" },
  { label: "PACK", tone: [255, 190, 70, 255], icon: "$" },
  { label: "CREW", tone: [90, 214, 120, 255], icon: "♥" },
  { label: "RUN", tone: [90, 150, 255, 255], icon: "▶" },
];

/** Command deck: prompt on the left, four icon buttons in a 2×2 grid on the right. */
export function commandDeck(g: Gfx, prompt: string, sub: string, sel: number, now: number, reduced: boolean, t: number): void {
  const { x, y, w, h } = DECK;
  const dy = slideIn(t, 40);
  glass(g, x, y + dy, w, h, { r: 7 });
  g.textShadow(x + 12, y + 9 + dy, prompt, UI.text, UI.ink);
  g.text(x + 12, y + 23 + dy, sub, UI.textDim);
  const kx = x + 12;
  const aw = hint(g, kx, y + 35 + dy, "A", "pick", "a", UI.textDim);
  hint(g, kx + aw + 8, y + 35 + dy, "B", "tactics", "b", UI.textDim);
  const bw = 84, bh = 20;
  CMDS.forEach((c, n) => {
    const bx = x + w - (2 - (n % 2)) * (bw + 4) - 4;
    const by = y + 4 + Math.floor(n / 2) * (bh + 3) + dy;
    const on = n === sel;
    const lift = on && !reduced ? -1 : 0;
    g.rrect(bx, by + 2, bw, bh, 6, [0, 0, 0, 80]);
    g.rrect(bx, by + lift, bw, bh, 6, on ? UI.ink : [20, 24, 44, 255]);
    g.gradV(bx + 1, by + 1 + lift, bw - 2, bh - 2, on ? mix(c.tone, UI.white, 0.25) : UI.bg2, on ? c.tone : UI.bg1, 5);
    g.hline(bx + 5, by + 1 + lift, bw - 10, withA(UI.white, on ? 150 : 50));
    g.disc(bx + 11, by + 10 + lift, 6, on ? withA(UI.white, 70) : withA(c.tone, 60));
    g.text(bx + 8, by + 7 + lift, c.icon, on ? UI.white : c.tone);
    g.text(bx + 21, by + 7 + lift, `${on ? ">" : " "}${c.label}`, on ? UI.paperInk : UI.text);
    if (on) focus(g, bx - 1, by - 1 + lift, bw + 2, bh + 2, now, reduced, 7);
  });
}

export interface MoveRow {
  name: string;
  type: TypeId;
  pp: number;
  max: number;
}

/** Four move buttons (2×2) + a detail card on the right. */
export function moveDeck(g: Gfx, rows: MoveRow[], sel: number, now: number, reduced: boolean, t: number): { cardX: number; cardY: number } {
  const { x, y, h } = DECK;
  const dy = slideIn(t, 40);
  const gridW = 250;
  glass(g, x, y + dy, gridW, h, { r: 7 });
  const bw = 119, bh = 20;
  rows.forEach((m, n) => {
    const bx = x + 4 + (n % 2) * (bw + 4);
    const by = y + 4 + Math.floor(n / 2) * (bh + 3) + dy;
    const on = n === sel;
    const col = TYPE_COL[m.type]!;
    g.rrect(bx, by, bw, bh, 6, on ? UI.ink : [20, 24, 44, 255]);
    g.gradV(bx + 1, by + 1, bw - 2, bh - 2, on ? mix(col.fill, UI.white, 0.2) : UI.bg2, on ? col.fill : UI.bg1, 5);
    g.rrect(bx + 3, by + 3, 4, bh - 6, 2, on ? UI.white : col.fill);
    const pp = `${m.pp}/${m.max}`;
    const low = m.pp <= Math.ceil(m.max / 4);
    const ppW = measureText(pp);
    g.text(bx + 11, by + 7, fitText(`${on ? ">" : " "}${m.name}`, bw - ppW - 22), on ? UI.paperInk : UI.text);
    g.text(bx + bw - ppW - 6, by + 7, pp, on ? UI.paperInk : low ? UI.red : UI.textDim);
    if (on) focus(g, bx - 1, by - 1, bw + 2, bh + 2, now, reduced, 7);
  });
  const cx = x + gridW + 4;
  glass(g, cx, y + dy, 372 - gridW - 4, h, { r: 7 });
  return { cardX: cx, cardY: y + dy };
}

/** Pack / crew list in the deck, four rows visible, with optional HP meters. */
export function listDeck(g: Gfx, items: string[], index: number, now: number, reduced: boolean, t: number, bars?: readonly (number | null)[], title?: string): void {
  const { x, y, w, h } = DECK;
  const dy = slideIn(t, 40);
  const rows = 4;
  const hh = h + 34;
  const yy = y - 34 + dy;
  glass(g, x, yy, w, hh, { r: 7 });
  if (title) g.text(x + 12, yy + 5, title, UI.textDim);
  const first = Math.max(0, Math.min(index - rows + 1, items.length - rows));
  items.slice(Math.max(0, first), Math.max(0, first) + rows).forEach((label, row) => {
    const i = Math.max(0, first) + row;
    const ry = yy + 16 + row * 16;
    const on = i === index;
    if (on) {
      g.rrect(x + 6, ry - 3, w - 12, 15, 5, withA(UI.gold, 50));
      g.rrectLine(x + 6, ry - 3, w - 12, 15, 5, withA(UI.gold, 200));
      cursor(g, x + 10, ry, now, reduced);
    }
    g.text(x + 22, ry, `${on ? ">" : " "}${label}`, on ? UI.goldHi : UI.text);
    const b = bars?.[i];
    if (b !== undefined && b !== null) meter(g, x + w - 96, ry + 1, 80, 6, b);
  });
  if (first > 0) g.text(x + w - 12, yy + 4, "▲", UI.textDim);
  if (first + rows < items.length) g.text(x + w - 12, yy + hh - 12, "▼", UI.textDim);
}

/** Full-screen card (tactics, evolution frame, etc.). */
export function card(g: Gfx, x: number, y: number, w: number, h: number, title: string, t: number, accent: Col = UI.teal): void {
  const dy = slideIn(t, 20);
  glass(g, x, y + dy, w, h, { r: 8 });
  g.gradV(x + 1, y + 1 + dy, w - 2, 18, withA(accent, 200), withA(mix(accent, UI.bg1, 0.6), 200), 7);
  g.textShadow(x + 10, y + 6 + dy, title, UI.white, UI.ink);
}

export function typeName(type: TypeId): string {
  return TYPE_META[type].name;
}
